import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Check, X, ChevronRight } from 'lucide-react';

const HUBSPOT_MEETING_LINK = 'https://meetings-eu1.hubspot.com/josh-dowlman';

// Service data configuration - easily editable per service
const servicesData = {
  'accounting-systems': {
    name: 'Accounting Systems & Reporting',
    slug: 'accounting-systems',
    heroSubheadline: 'Structured financial systems and reporting that give you full visibility and control over your business finances.',
    problemPoints: [
      'Scattered bookkeeping across multiple tools with no single source of truth',
      'Month-end close taking weeks instead of days',
      'Management reports that arrive too late to act on',
      'Year-end panic and scrambling for missing documentation',
      'No clear view of cash position, margins, or profitability'
    ],
    steps: [
      {
        number: '01',
        title: 'Audit & Review',
        description: 'We assess your existing systems, chart of accounts, reporting gaps, and compliance exposure to understand exactly where you stand.'
      },
      {
        number: '02',
        title: 'System Architecture',
        description: 'We build structured cloud accounting workflows aligned with your business model, integrating your sales channels, payment processors, and expense management.'
      },
      {
        number: '03',
        title: 'Monthly Financials',
        description: 'You receive clean, accurate monthly reports with management-level clarity—profit & loss, balance sheet, and cash flow delivered consistently.'
      },
      {
        number: '04',
        title: 'Year-End Preparation',
        description: 'Your year-end financials are prepared without panic or last-minute chaos. Everything is structured, documented, and ready for filing.'
      },
      {
        number: '05',
        title: 'Ongoing Oversight',
        description: 'We continuously monitor, refine, and ensure accuracy. Your financial systems evolve as your business grows.'
      }
    ],
    benefits: [
      'Clear financial visibility at any moment',
      'No compliance stress or missed deadlines',
      'Accurate year-end reporting ready weeks in advance',
      'Proactive insights that inform decisions',
      'Structured foundation for sustainable growth'
    ],
    targetAudience: [
      'Ecommerce founders scaling beyond six figures',
      'Creators earning multi-channel income',
      'Web3 operators managing digital assets',
      'Businesses outgrowing spreadsheet accounting'
    ]
  },
  'tax-planning': {
    name: 'Tax Planning & Compliance',
    slug: 'tax-planning',
    heroSubheadline: 'Strategic tax structuring and compliance that minimizes liability while keeping you fully compliant.',
    problemPoints: [
      'Paying more tax than legally necessary due to poor planning',
      'Year-end surprises with unexpected tax bills',
      'Missing legitimate deductions and reliefs',
      'No clear strategy for director compensation or dividends',
      'Cross-border complexity creating compliance risk'
    ],
    steps: [
      {
        number: '01',
        title: 'Tax Position Review',
        description: 'We analyze your current tax position, structure, and historical filings to identify immediate opportunities and compliance gaps.'
      },
      {
        number: '02',
        title: 'Strategic Planning',
        description: 'We develop a tax strategy tailored to your business model—optimizing for salary, dividends, pension contributions, and business expenses.'
      },
      {
        number: '03',
        title: 'Quarterly Monitoring',
        description: 'We track your tax position throughout the year, making adjustments as revenue fluctuates and opportunities arise.'
      },
      {
        number: '04',
        title: 'Compliant Filing',
        description: 'All returns—corporation tax, self-assessment, VAT—are prepared accurately and filed well before deadlines.'
      },
      {
        number: '05',
        title: 'Ongoing Optimization',
        description: 'Tax law changes constantly. We keep you ahead of legislative updates that affect your business.'
      }
    ],
    benefits: [
      'Minimized tax liability within legal boundaries',
      'No year-end surprises or unexpected bills',
      'Full compliance across all tax obligations',
      'Clear understanding of your effective tax rate',
      'Proactive planning that compounds over time'
    ],
    targetAudience: [
      'Business owners paying significant tax',
      'Directors optimizing salary and dividend mix',
      'Founders with complex equity structures',
      'International operators with cross-border income'
    ]
  },
  'growth-advisory': {
    name: 'Growth & Profit Advisory',
    slug: 'growth-advisory',
    heroSubheadline: 'Strategic financial guidance that helps you scale profitably and make confident business decisions.',
    problemPoints: [
      'Growing revenue but not seeing proportional profit',
      'Making decisions based on gut feel rather than data',
      'No clear understanding of unit economics or margins',
      'Cash flow surprises despite strong sales',
      'Scaling without knowing which products or channels actually make money'
    ],
    steps: [
      {
        number: '01',
        title: 'Profitability Analysis',
        description: 'We break down your revenue streams, cost structure, and margins to reveal where profit is actually generated—and where it leaks.'
      },
      {
        number: '02',
        title: 'Cash Flow Modeling',
        description: 'We build forward-looking cash flow projections so you can plan inventory, hiring, and investment with confidence.'
      },
      {
        number: '03',
        title: 'KPI Framework',
        description: 'We establish the key metrics that matter for your business model, with dashboards that surface actionable insights.'
      },
      {
        number: '04',
        title: 'Strategic Advisory',
        description: 'Regular strategy sessions to review performance, pressure-test decisions, and refine your growth roadmap.'
      },
      {
        number: '05',
        title: 'Scenario Planning',
        description: 'We model different growth scenarios so you understand the financial implications before committing resources.'
      }
    ],
    benefits: [
      'Clear visibility into true profitability',
      'Confident decision-making backed by data',
      'Predictable cash flow even during growth',
      'Understanding of which products and channels perform',
      'Strategic partner invested in your success'
    ],
    targetAudience: [
      'Founders preparing for funding or exit',
      'Ecommerce brands scaling past £500k revenue',
      'Businesses expanding into new markets',
      'Operators seeking strategic financial guidance'
    ]
  },
  'crypto-accounting': {
    name: 'Digital Asset & Crypto Accounting',
    slug: 'crypto-accounting',
    heroSubheadline: 'Specialized accounting for crypto assets, DeFi activity, and Web3 operations with full regulatory compliance.',
    problemPoints: [
      'Thousands of transactions across multiple wallets and chains',
      'No clear cost basis tracking for capital gains',
      'DeFi positions creating accounting complexity',
      'Regulatory uncertainty and compliance anxiety',
      'Traditional accountants who don\'t understand crypto'
    ],
    steps: [
      {
        number: '01',
        title: 'Wallet Discovery',
        description: 'We identify and connect all your wallets, exchanges, and DeFi positions to build a complete picture of your crypto holdings.'
      },
      {
        number: '02',
        title: 'Transaction Reconciliation',
        description: 'We categorize and reconcile every transaction—trades, transfers, staking rewards, airdrops, and DeFi interactions.'
      },
      {
        number: '03',
        title: 'Cost Basis Tracking',
        description: 'We establish accurate cost basis for every position using HMRC-compliant methodologies, essential for capital gains reporting.'
      },
      {
        number: '04',
        title: 'Tax Reporting',
        description: 'We prepare crypto-specific tax reports and integrate them with your broader tax position for unified compliance.'
      },
      {
        number: '05',
        title: 'Ongoing Monitoring',
        description: 'As regulations evolve, we keep your reporting compliant and advise on tax-efficient strategies for crypto activities.'
      }
    ],
    benefits: [
      'Complete visibility across all crypto holdings',
      'Accurate capital gains calculations',
      'Full regulatory compliance and audit trail',
      'Clear reporting for both crypto and fiat',
      'Guidance from accountants who understand Web3'
    ],
    targetAudience: [
      'Crypto traders with significant activity',
      'DeFi participants with complex positions',
      'NFT creators and collectors',
      'Web3 founders and DAO contributors'
    ]
  }
};

// Header Component (reused from homepage)
const Header = () => (
  <header className="fixed top-0 left-0 right-0 bg-parchment/95 backdrop-blur-sm z-50 border-b border-iron-grey">
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-4 flex items-center justify-between">
      <Link to="/" className="font-bold text-obsidian tracking-wide" style={{ fontFamily: "'Glacial Indifference', sans-serif", fontSize: '16pt' }} data-testid="logo">
        OAKMERE ADVISORY
      </Link>
      <nav className="hidden md:flex items-center gap-8">
        <Link to="/#services" className="font-inter text-sm text-obsidian hover:text-graphite transition-colors">Services</Link>
        <Link to="/#pricing" className="font-inter text-sm text-obsidian hover:text-graphite transition-colors">Pricing</Link>
        <Link to="/login" className="font-inter text-sm text-obsidian hover:text-graphite transition-colors">
          Client Login
        </Link>
        <a href={HUBSPOT_MEETING_LINK} target="_blank" rel="noopener noreferrer" className="btn-primary text-xs py-2 px-4">
          Book a Call
        </a>
      </nav>
      <Link to="/login" className="md:hidden font-inter text-sm text-obsidian">
        Login
      </Link>
    </div>
  </header>
);

// Footer Component (reused from homepage)
const Footer = () => (
  <footer className="bg-parchment border-t border-iron-grey py-12 px-6 md:px-12 lg:px-24">
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
        <div>
          <span className="text-[16px] font-bold text-obsidian tracking-widest" style={{ fontFamily: "'Glacial Indifference', sans-serif" }}>
            OAKMERE ADVISORY
          </span>
          <p className="font-inter text-sm text-iron-grey mt-2">
            Structured accounting for the digital economy.
          </p>
        </div>
        <div>
          <span className="font-inter text-sm uppercase tracking-wider text-obsidian mb-4 block">Services</span>
          <ul className="space-y-2">
            {Object.values(servicesData).map((service) => (
              <li key={service.slug}>
                <Link to={`/services/${service.slug}`} className="font-inter text-sm text-iron-grey hover:text-obsidian transition-colors">
                  {service.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <span className="font-inter text-sm uppercase tracking-wider text-obsidian mb-4 block">Company</span>
          <ul className="space-y-2">
            <li><Link to="/#pricing" className="font-inter text-sm text-iron-grey hover:text-obsidian transition-colors">Pricing</Link></li>
            <li><a href="#contact" className="font-inter text-sm text-iron-grey hover:text-obsidian transition-colors">Contact</a></li>
          </ul>
        </div>
        <div>
          <span className="font-inter text-sm uppercase tracking-wider text-obsidian mb-4 block">Portal</span>
          <ul className="space-y-2">
            <li><Link to="/login" className="font-inter text-sm text-iron-grey hover:text-obsidian transition-colors">Client Login</Link></li>
            <li><Link to="/register" className="font-inter text-sm text-iron-grey hover:text-obsidian transition-colors">Create Account</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-iron-grey pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
        <p className="font-inter text-xs text-iron-grey">
          © {new Date().getFullYear()} Oakmere Advisory. All rights reserved.
        </p>
        <div className="flex gap-6">
          <a href="#privacy" className="font-inter text-xs text-iron-grey hover:text-obsidian transition-colors">Privacy Policy</a>
          <a href="#terms" className="font-inter text-xs text-iron-grey hover:text-obsidian transition-colors">Terms of Service</a>
        </div>
      </div>
    </div>
  </footer>
);

// Hero Section
const HeroSection = ({ name, subheadline }) => (
  <section className="bg-parchment pt-32 pb-16 px-6 md:px-12 lg:px-24" data-testid="service-hero">
    <div className="max-w-4xl mx-auto">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 mb-8">
        <Link to="/" className="font-inter text-sm text-iron-grey hover:text-obsidian transition-colors">Home</Link>
        <ChevronRight size={14} className="text-iron-grey" />
        <Link to="/#services" className="font-inter text-sm text-iron-grey hover:text-obsidian transition-colors">Services</Link>
        <ChevronRight size={14} className="text-iron-grey" />
        <span className="font-inter text-sm text-obsidian">{name}</span>
      </nav>
      
      <h1 className="font-fraunces font-bold text-4xl sm:text-5xl lg:text-6xl text-obsidian mb-6 leading-tight">
        {name}
      </h1>
      <p className="font-inter text-lg md:text-xl text-graphite mb-8 max-w-2xl leading-relaxed">
        {subheadline}
      </p>
      <div className="flex flex-col sm:flex-row gap-4">
        <a 
          href={HUBSPOT_MEETING_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary inline-flex items-center justify-center gap-2"
          data-testid="hero-cta-primary"
        >
          Book a Strategic Call
          <ArrowRight size={16} strokeWidth={1.5} />
        </a>
        <Link 
          to="/#pricing" 
          className="btn-secondary inline-flex items-center justify-center gap-2"
          data-testid="hero-cta-secondary"
        >
          View Pricing
        </Link>
      </div>
    </div>
  </section>
);

// Problem Section
const ProblemSection = ({ problemPoints }) => (
  <section className="bg-white py-16 md:py-24 px-6 md:px-12 lg:px-24 border-y border-iron-grey" data-testid="problem-section">
    <div className="max-w-4xl mx-auto">
      <h2 className="font-fraunces font-bold text-3xl md:text-4xl text-obsidian mb-8">
        Why most businesses get this wrong.
      </h2>
      <div className="space-y-4">
        {problemPoints.map((point) => (
          <div key={point} className="flex items-start gap-4 p-4 bg-parchment border-l-2 border-iron-grey">
            <X size={20} className="text-iron-grey flex-shrink-0 mt-0.5" strokeWidth={1.5} />
            <p className="font-inter text-graphite leading-relaxed">{point}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Approach Section
const ApproachSection = ({ steps }) => (
  <section className="bg-parchment py-16 md:py-24 px-6 md:px-12 lg:px-24" data-testid="approach-section">
    <div className="max-w-4xl mx-auto">
      <h2 className="font-fraunces font-bold text-3xl md:text-4xl text-obsidian mb-12">
        How we handle it.
      </h2>
      <div className="space-y-0 border border-iron-grey">
        {steps.map((step, index) => (
          <div 
            key={step.number} 
            className={`p-6 md:p-8 bg-white ${index < steps.length - 1 ? 'border-b border-iron-grey' : ''}`}
          >
            <div className="flex items-start gap-6">
              <span className="font-inter text-xs text-iron-grey mt-1">{step.number}</span>
              <div>
                <h3 className="font-fraunces font-bold text-xl md:text-2xl text-obsidian mb-3">
                  {step.title}
                </h3>
                <p className="font-inter text-graphite leading-relaxed">
                  {step.description}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Benefits Section
const BenefitsSection = ({ benefits }) => (
  <section className="bg-white py-16 md:py-24 px-6 md:px-12 lg:px-24 border-y border-iron-grey" data-testid="benefits-section">
    <div className="max-w-4xl mx-auto">
      <h2 className="font-fraunces font-bold text-3xl md:text-4xl text-obsidian mb-8">
        What this means for you.
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {benefits.map((benefit) => (
          <div key={benefit} className="flex items-start gap-3 p-4 bg-parchment">
            <Check size={20} className="text-obsidian flex-shrink-0 mt-0.5" strokeWidth={2} />
            <p className="font-inter text-obsidian">{benefit}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Target Audience Section
const AudienceSection = ({ targetAudience }) => (
  <section className="bg-parchment py-16 md:py-24 px-6 md:px-12 lg:px-24" data-testid="audience-section">
    <div className="max-w-4xl mx-auto">
      <h2 className="font-fraunces font-bold text-3xl md:text-4xl text-obsidian mb-8">
        Who this is for.
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {targetAudience.map((audience) => (
          <div key={audience} className="flex items-center gap-3 p-4 bg-white border border-iron-grey">
            <div className="w-2 h-2 bg-obsidian flex-shrink-0" />
            <p className="font-inter text-obsidian">{audience}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Differentiator Section
const DifferentiatorSection = () => (
  <section className="bg-white py-16 md:py-24 px-6 md:px-12 lg:px-24 border-y border-iron-grey" data-testid="differentiator-section">
    <div className="max-w-4xl mx-auto">
      <h2 className="font-fraunces font-bold text-3xl md:text-4xl text-obsidian mb-8">
        Why Oakmere Advisory.
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[
          {
            title: 'Digital-Native Expertise',
            description: 'We understand ecommerce platforms, payment processors, and digital business models inside and out.'
          },
          {
            title: 'Structured Onboarding',
            description: 'Clear checklists and processes mean you always know what\'s needed and when.'
          },
          {
            title: 'Real-Time Reporting',
            description: 'No waiting months for financials. Your dashboard updates as your business moves.'
          },
          {
            title: 'Strategic Approach',
            description: 'We don\'t just process numbers—we help you make better decisions with them.'
          },
          {
            title: 'Financial Dashboard',
            description: 'Proprietary client portal with clear visibility into your financial position.'
          },
          {
            title: 'Direct Advisor Access',
            description: 'No call centers or ticket queues. Direct access to your dedicated advisor.'
          }
        ].map((item) => (
          <div key={item.title} className="p-6 bg-parchment border-l-2 border-obsidian">
            <h3 className="font-fraunces font-bold text-lg text-obsidian mb-2">{item.title}</h3>
            <p className="font-inter text-sm text-graphite leading-relaxed">{item.description}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Final CTA Section
const FinalCTASection = () => (
  <section className="bg-obsidian py-16 md:py-24 px-6 md:px-12 lg:px-24" data-testid="final-cta-section">
    <div className="max-w-4xl mx-auto text-center">
      <h2 className="font-fraunces font-bold text-3xl md:text-4xl text-parchment mb-8">
        Ready to bring structure to your finances?
      </h2>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <a 
          href={HUBSPOT_MEETING_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="bg-parchment text-obsidian px-8 py-4 font-inter font-medium uppercase tracking-wider text-sm inline-flex items-center justify-center gap-2 hover:bg-white transition-colors duration-200"
          data-testid="final-cta-primary"
        >
          Book a Strategic Call
          <ArrowRight size={16} strokeWidth={1.5} />
        </a>
        <a 
          href="/year-end-prep-guide.pdf" 
          download
          className="bg-transparent text-parchment px-8 py-4 font-inter font-medium uppercase tracking-wider text-sm border border-parchment inline-flex items-center justify-center hover:bg-parchment hover:text-obsidian transition-colors duration-200"
          data-testid="final-cta-secondary"
        >
          Financial Year End Prep Guide
        </a>
      </div>
    </div>
  </section>
);

// Main Service Detail Page Component
const ServiceDetailPage = () => {
  const { serviceSlug } = useParams();
  const service = servicesData[serviceSlug];

  // Handle invalid service slug
  if (!service) {
    return (
      <div className="min-h-screen bg-parchment">
        <Header />
        <main className="pt-32 pb-16 px-6 md:px-12 lg:px-24 text-center">
          <h1 className="font-fraunces font-bold text-4xl text-obsidian mb-4">Service Not Found</h1>
          <p className="font-inter text-graphite mb-8">The service you're looking for doesn't exist.</p>
          <Link to="/#services" className="btn-primary inline-flex items-center gap-2">
            View All Services
            <ArrowRight size={16} strokeWidth={1.5} />
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-parchment" data-testid={`service-page-${serviceSlug}`}>
      <Header />
      <main>
        <HeroSection name={service.name} subheadline={service.heroSubheadline} />
        <ProblemSection problemPoints={service.problemPoints} />
        <ApproachSection steps={service.steps} />
        <BenefitsSection benefits={service.benefits} />
        <AudienceSection targetAudience={service.targetAudience} />
        <DifferentiatorSection />
        <FinalCTASection />
      </main>
      <Footer />
    </div>
  );
};

export default ServiceDetailPage;
export { servicesData };
